const BASE = '/api'

// Module-level token for Authorization header fallback (set by auth context, never from localStorage)
let _apiToken: string | null = null

/** Called by AuthProvider to keep the in-memory token in sync. */
export function setApiToken(token: string | null): void {
  _apiToken = token
}

export async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(_apiToken ? { Authorization: `Bearer ${_apiToken}` } : {}),
      ...options?.headers,
    },
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }))
    throw new Error(err.detail || res.statusText)
  }
  return res.json()
}

// In-memory API response cache
const apiCache = new Map<string, { data: unknown; timestamp: number }>()

export async function cachedRequest<T>(path: string, options: RequestInit | undefined, ttl: number): Promise<T> {
  // Only cache GET requests (method undefined or 'GET')
  const method = options?.method?.toUpperCase()
  if (method && method !== 'GET') {
    return request<T>(path, options)
  }

  const now = Date.now()
  const cached = apiCache.get(path)
  if (cached && (now - cached.timestamp) < ttl) {
    return cached.data as T
  }

  const data = await request<T>(path, options)
  apiCache.set(path, { data, timestamp: now })
  return data
}

/** Remove a specific path from the cache, or clear the entire cache if no path given. */
export function clearCache(path?: string): void {
  if (path) {
    apiCache.delete(path)
  } else {
    apiCache.clear()
  }
}

/** Invalidate all cache entries whose keys start with the given prefix. */
export function invalidateCacheByPrefix(prefix: string): void {
  for (const key of apiCache.keys()) {
    if (key.startsWith(prefix)) {
      apiCache.delete(key)
    }
  }
}

/** Access the current in-memory API token (needed by multipart upload functions). */
export function getApiToken(): string | null {
  return _apiToken
}
